import { useState, useEffect, useRef } from "react";

import worldmap from "../assets/worldmap.png";
import { SuccessModal } from "../components/SuccessModal";
import {
  faBriefcase,
  faChevronDown,
  faEnvelope,
  faLocationDot,
  faStar,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const contactCards = [
  {
    icon: faEnvelope,
    title: "GENERAL ENQUIRIES",
    email: "hello@luupli.com",
  },
  {
    icon: faStar,
    title: "CREATOR ENQUIRIES",
    email: "creators@luupli.com",
  },
  {
    icon: faBriefcase,
    title: "INVESTORS",
    email: "invest@luupli.com",
  },
  {
    icon: faBriefcase,
    title: "BUSINESS",
    email: "advertise@luupli.com",
  },
  {
    icon: faUser,
    title: "RECRUITMENT & JOBS",
    email: "recruitment@luupli.com",
  },
];

const socialLinks = [
  {
    icon: faFacebook,
    href: "https://web.facebook.com/profile.php?id=100095175349473",
    label: "Facebook",
    className: "text-[#1877F2]",
  },
  {
    icon: faInstagram,
    href: "https://instagram.com/luuplihq/",
    label: "Instagram",
    className: "text-[#E4405F]",
  },
  {
    icon: faTiktok,
    href: "https://www.tiktok.com/@luupli",
    label: "TikTok",
    className: "text-[#111111]",
  },
  {
    icon: faLinkedin,
    href: "https://www.linkedin.com/company/luupli/",
    label: "LinkedIn",
    className: "text-[#0A66C2]",
  },
];

const howDidYouHearOptions = [
  "Referral",
  "Social Media",
  "Online Search",
  "Creator Community",
  "Investors community",
];

const initialUserInfo = {
  name: "",
  email: "",
  message: "",
  phoneNumber: "",
  howDidYouFindUs: "",
};

const ContactFooterSection = () => {
  const isProd = process.env.REACT_APP_ENV === "production";
  const privacyPolicy = isProd ? process.env.REACT_APP_PRIVACY_PROD : process.env.REACT_APP_PRIVACY_DEV || "https://legal.luupli.com/PrivacyPolicy/";
  const termsAndConditions = isProd ? process.env.REACT_APP_TERMS_LINK_PROD : process.env.REACT_APP_TERMS_LINK_DEV || "https://legal.luupli.com/";
  const contactEndpoint = process.env.REACT_APP_CONTACT_FORM_URL;
  const [userInfo, setUserInfo] = useState(initialUserInfo);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [howDidYouHearOpen, setHowDidYouHearOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorToast, setErrorToast] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const howDidYouHearRef = useRef(null);

  useEffect(() => {
    if (!errorToast) return;
    const t = setTimeout(() => setErrorToast(null), 5000);
    return () => clearTimeout(t);
  }, [errorToast]);

  useEffect(() => {
    if (!howDidYouHearOpen) return;
    const handleClickOutside = (e) => {
      if (howDidYouHearRef.current && howDidYouHearRef.current.contains(e.target)) return;
      setHowDidYouHearOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [howDidYouHearOpen]);

  const handleChange = (e) => {
    setUserInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+?[\d\s\-()]{7,20}$/;
    return phoneRegex.test(phone.trim());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validatePhone(userInfo.phoneNumber)) {
      setPhoneError("Please enter a valid phone number");
      return;
    }
    setPhoneError("");
    setIsSubmitting(true);
    setErrorToast(null);
    const userInfoJSON = JSON.stringify(userInfo);
    const submittedName = userInfo.name;
    fetch(contactEndpoint, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: userInfoJSON,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.result === "success") {
          setUserInfo(initialUserInfo);
          setShowSuccessModal(submittedName || true);
        } else {
          setErrorToast(data.message || "Something went wrong. Please try again.");
        }
      })
      .catch(() => {
        setUserInfo(initialUserInfo);
        setShowSuccessModal(submittedName || true);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <>
    <section id="contact" className="bg-white border-t border-[#EAEAEA]">
      <div className="max-w-[1520px] mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-20 lg:py-24 xl:py-28 2xl:py-32 flex flex-col gap-[15px]">
        <h2 className="text-center font-sans text-[clamp(28px,4vw,44px)] md:text-[clamp(36px,5vw,52px)] leading-[1.2] font-medium text-[#111111] max-w-[900px] mx-auto">
          Creating a global community thats open, fair and full of happiness for everyone
        </h2>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr_1fr] gap-8 lg:gap-10">
          <div>
            <h3 className="font-sans text-xl md:text-2xl font-bold leading-tight text-[#111]">
              Get in Touch
            </h3>
            <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
              <input
                name="name"
                type="text"
                placeholder="Name"
                required
                value={userInfo.name}
                onChange={handleChange}
                className="w-full h-12 rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-normal text-[#111] placeholder:text-[#9ca3af]"
              />
              <div>
                <input
                  name="phoneNumber"
                  type="tel"
                  placeholder="Phone number"
                  required
                  autoComplete="off"
                  value={userInfo.phoneNumber}
                  onChange={(e) => {
                    const filtered = e.target.value.replace(/[a-zA-Z]/g, "");
                    setUserInfo((prev) => ({ ...prev, phoneNumber: filtered }));
                    if (phoneError) setPhoneError("");
                  }}
                  className={`w-full h-12 rounded-xl border bg-white px-4 text-sm font-normal text-[#111] placeholder:text-[#9ca3af] ${phoneError ? "border-red-400" : "border-[#d9d9d9]"}`}
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{phoneError}</p>
                )}
              </div>
              <input
                name="email"
                type="email"
                placeholder="Email"
                required
                value={userInfo.email}
                onChange={handleChange}
                className="w-full h-12 rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-normal text-[#111] placeholder:text-[#9ca3af]"
              />
              <div ref={howDidYouHearRef} className="relative">
                <div
                  className="w-full h-12 rounded-xl border border-[#d9d9d9] bg-white px-4 flex items-center justify-between text-sm font-normal text-[#6b7280] cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => setHowDidYouHearOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setHowDidYouHearOpen((v) => !v);
                    }
                  }}
                >
                  <span className={userInfo.howDidYouFindUs ? "text-[#111]" : ""}>
                    {userInfo.howDidYouFindUs || "How did you hear about us?"}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} className="w-4 h-4 text-[#6b7280] shrink-0 ml-2" />
                </div>
                {howDidYouHearOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 rounded-xl border border-[#d9d9d9] bg-white shadow-lg py-1 z-10">
                    {howDidYouHearOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className="w-full text-left px-4 py-2.5 text-sm text-[#111] hover:bg-[#f3f4f6] first:rounded-t-xl last:rounded-b-xl"
                        onClick={() => {
                          setUserInfo((prev) => ({ ...prev, howDidYouFindUs: option }));
                          setHowDidYouHearOpen(false);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <textarea
                name="message"
                required
                value={userInfo.message}
                onChange={handleChange}
                className="w-full h-24 rounded-xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-normal text-[#111] placeholder:text-[#9ca3af] resize-none"
                placeholder="Write your message here"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 rounded-[18px] bg-[#0B0D0B] text-white text-sm font-normal px-12 py-2.5 shadow-[0_4px_8px_rgba(0,0,0,0.16)] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>

          <div>
            <h3 className="font-sans text-xl md:text-2xl font-bold leading-tight text-[#111]">
              Address
            </h3>
            <div className="mt-4 overflow-hidden h-[200px] md:h-[260px]">
              <img
                src={worldmap}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="mt-4 flex items-start gap-3 text-[#111]">
              <FontAwesomeIcon icon={faLocationDot} className="w-5 h-5 text-[#10835b] mt-0.5 shrink-0" />
              <p className="font-sans text-sm leading-relaxed font-normal text-[#374151]">
                Luupli Limited
                <br />
                86-90 Paul Street London, EC2A 4NE
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-sans text-xl md:text-2xl font-bold leading-tight text-[#111]">
              Contact Emails
            </h3>
            <div className="mt-4 space-y-3">
              {contactCards.map((item) => (
                <div key={item.email} className="rounded-xl border border-[#dfdfdf] bg-[#f9fafb] px-4 py-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#10835b] flex items-center justify-center shrink-0">
                    <FontAwesomeIcon icon={item.icon} className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-sans text-xs font-medium text-[#6b7280] uppercase tracking-wide">{item.title}</p>
                    <a href={`mailto:${item.email}`} className="font-sans text-sm font-normal text-[#111] hover:underline block truncate">
                      {item.email}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[#EAEAEA] flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a href={termsAndConditions} target="_blank" rel="noopener noreferrer" className="font-sans text-xs font-normal text-[#374151] underline">
              Terms of Service
            </a>
            <a href={privacyPolicy} target="_blank" rel="noopener noreferrer" className="font-sans text-xs font-normal text-[#374151] underline">
              Privacy Policy
            </a>
          </div>

          <div className="flex items-center gap-4">
            {socialLinks.map((link) => (
              <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" aria-label={link.label} className={link.className}>
                <FontAwesomeIcon icon={link.icon} className="w-8 h-8" />
              </a>
            ))}
          </div>

          <p className="font-sans text-xs font-normal text-[#374151]">
            &copy; {new Date().getFullYear()} Luupli Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </section>
    {showSuccessModal && (
      <SuccessModal name={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
    )}
    {errorToast && (
      <div
        role="alert"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl bg-[#0B0D0B] text-white text-sm shadow-lg max-w-[90vw] flex items-center gap-3"
      >
        <span>{errorToast}</span>
        <button
          type="button"
          onClick={() => setErrorToast(null)}
          className="shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    )}
    </>
  );
};

export default ContactFooterSection;
